
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { UseFormReturn } from "react-hook-form";
import { FormData } from "@/types/document-generator";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface FileUploadSectionProps {
  form: UseFormReturn<FormData>;
  onContentRead?: (content: string) => void;
}

export function FileUploadSection({ form, onContentRead }: FileUploadSectionProps) {
  const { toast } = useToast();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/plain' && file.type !== 'application/pdf') {
      toast({
        variant: "destructive",
        title: "Ugyldig filtype",
        description: "Kun .txt og .pdf filer er støttet.",
      });
      return;
    }

    try {
      const text = await file.text();
      if (onContentRead) {
        onContentRead(text);
      }
      // Set the content in the form
      form.setValue("content", text);
      // Set a default title if none exists
      if (!form.getValues("title")) {
        form.setValue("title", file.name.replace(/\.[^/.]+$/, ""));
      }
    } catch (error) {
      console.error('Error reading file:', error);
      toast({
        variant: "destructive",
        title: "Feil ved lesing av fil",
        description: "Kunne ikke lese filinnholdet. Prøv igjen.",
      });
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <CardTitle className="text-base sm:text-lg">Last opp dokument for analyse</CardTitle>
        <CardDescription className="text-sm">
          Last opp en .txt eller .pdf fil for å analysere innholdet
        </CardDescription>
      </CardHeader>
      <CardContent>
        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <Upload className="w-8 h-8 mb-2 text-gray-400" />
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">Klikk for å laste opp</span> eller dra og slipp
            </p>
            <p className="text-xs text-gray-500">.txt eller .pdf</p>
          </div>
          <input
            type="file"
            className="hidden"
            accept=".txt,.pdf"
            onChange={handleFileChange}
          />
        </label>
      </CardContent>
    </Card>
  );
}
